import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SuggestionCard } from './SuggestionCard.tsx';
import type { SuggestionItem } from './SuggestionCard.tsx';
import { SuggestionReviewPanel } from './review-panel/SuggestionReviewPanel.tsx';

type NeedsReviewSectionProps = {
	items: SuggestionItem[];
	onReview: () => void;
};

export function NeedsReviewSection({ items, onReview }: NeedsReviewSectionProps) {
	const { formatMessage } = useIntl();
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	const handleReview = useCallback(() => {
		setIsPanelOpen(true);
		onReview();
	}, [onReview]);

	const handleClosePanel = useCallback(() => {
		setIsPanelOpen(false);
	}, []);

	const renderTrigger = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				{...triggerProps}
				ref={triggerRef}
				icon={ShowMoreHorizontalIcon}
				label={formatMessage(i18n.moreOptions)}
				appearance="subtle"
			/>
		),
		[formatMessage],
	);

	return (
		<>
			{isPanelOpen && (
				<SuggestionReviewPanel
					onClose={handleClosePanel}
					onCancel={handleClosePanel}
					onApprove={handleClosePanel}
				>
					{null}
				</SuggestionReviewPanel>
			)}
			<Box xcss={styles.container}>
				<Box xcss={styles.title}>
					<Inline alignBlock="center" spread="space-between">
						<Heading size="small">
							{formatMessage(i18n.needsReviewTitle, { count: items.length })}
						</Heading>
						<DropdownMenu trigger={renderTrigger}>
							<DropdownItem>{formatMessage(i18n.placeholderItem)}</DropdownItem>
						</DropdownMenu>
					</Inline>
				</Box>
				<Stack space="space.100">
					{items.map((item, index) => (
						// TODO: use a stable unique id from item once real data is available
						// eslint-disable-next-line react/no-array-index-key
						<SuggestionCard key={index} item={item} onReview={handleReview} />
					))}
				</Stack>
			</Box>
		</>
	);
}

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	title: {
		marginBottom: token('space.150'),
	},
});

const i18n = defineMessages({
	needsReviewTitle: {
		id: 'space-suggestions.needs-review.title',
		defaultMessage: '{count, plural, one {# needs review} other {# need review}}',
		description:
			'Title for the "needs review" section, showing how many suggestions are awaiting review',
	},
	moreOptions: {
		id: 'space-suggestions.needs-review.more-options',
		defaultMessage: 'More options',
		description:
			'Accessible label for the icon button that opens additional options for the needs review section',
	},
	placeholderItem: {
		id: 'space-suggestions.needs-review.placeholder-item',
		defaultMessage: 'Placeholder item',
		description: 'Placeholder dropdown menu item — to be replaced with real actions',
	},
});
